import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Search, 
  Podcast, 
  Play,
  ExternalLink,
  Music,
  Youtube,
  Instagram,
  Twitter
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GENRES = [
  { id: 'all', name: 'All Genres' },
  { id: 'business', name: 'Business & Entrepreneurship' },
  { id: 'culture', name: 'Culture & Society' },
  { id: 'faith', name: 'Faith & Spirituality' },
  { id: 'entertainment', name: 'Entertainment' },
  { id: 'education', name: 'Education' },
  { id: 'health', name: 'Health & Wellness' },
  { id: 'news', name: 'News & Politics' },
  { id: 'sports', name: 'Sports' },
  { id: 'tech', name: 'Technology' },
];

const PodcastCard = ({ podcast }) => {
  const defaultCover = 'https://images.unsplash.com/photo-1478737270239-2f02b77fc618?w=400&q=80';
  
  return (
    <div className="bg-white rounded-xl border border-stone-200 overflow-hidden hover:shadow-lg transition-all duration-300 group" data-testid={`podcast-card-${podcast.podcast_id}`}>
      <div className="relative aspect-square">
        <img
          src={podcast.cover_image_url || defaultCover}
          alt={podcast.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        
        {podcast.is_featured && (
          <Badge className="absolute top-3 left-3 bg-legacy-gold text-white">
            Featured
          </Badge>
        )}
        
        <div className="absolute bottom-3 left-3 right-3">
          <Badge variant="secondary" className="bg-white/90 text-emerald-deep">
            {podcast.genre}
          </Badge>
        </div>
      </div>
      
      <div className="p-5">
        <h3 className="font-display text-lg font-semibold text-stone-900 mb-1 line-clamp-1">
          {podcast.title}
        </h3>
        <p className="text-sm text-emerald-deep font-medium mb-2">
          Hosted by {podcast.host_name}
        </p>
        <p className="text-stone-600 text-sm line-clamp-2 mb-4">
          {podcast.description}
        </p>
        
        {/* Platform Links */}
        <div className="flex flex-wrap gap-2">
          {podcast.spotify_url && (
            <a
              href={podcast.spotify_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 px-3 py-1.5 bg-[#1DB954]/10 text-[#1DB954] rounded-full text-xs font-medium hover:bg-[#1DB954]/20 transition-colors"
            >
              <Music className="w-3 h-3" />
              Spotify
            </a>
          )}
          {podcast.apple_podcasts_url && (
            <a
              href={podcast.apple_podcasts_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 px-3 py-1.5 bg-purple-100 text-purple-600 rounded-full text-xs font-medium hover:bg-purple-200 transition-colors"
            >
              <Podcast className="w-3 h-3" />
              Apple
            </a>
          )}
          {podcast.youtube_url && (
            <a
              href={podcast.youtube_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 px-3 py-1.5 bg-red-100 text-red-600 rounded-full text-xs font-medium hover:bg-red-200 transition-colors"
            >
              <Youtube className="w-3 h-3" />
              YouTube
            </a>
          )}
          {podcast.soundcloud_url && (
            <a
              href={podcast.soundcloud_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 px-3 py-1.5 bg-orange-100 text-orange-600 rounded-full text-xs font-medium hover:bg-orange-200 transition-colors"
            >
              <Play className="w-3 h-3" />
              SoundCloud
            </a>
          )}
        </div>
        
        {/* Social Links */}
        <div className="flex gap-2 mt-3 pt-3 border-t border-stone-100">
          {podcast.instagram_url && (
            <a
              href={podcast.instagram_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-stone-400 hover:text-pink-500 transition-colors"
            >
              <Instagram className="w-4 h-4" />
            </a>
          )}
          {podcast.twitter_url && (
            <a
              href={podcast.twitter_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-stone-400 hover:text-blue-400 transition-colors"
            >
              <Twitter className="w-4 h-4" />
            </a>
          )}
          {podcast.website_url && (
            <a
              href={podcast.website_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-stone-400 hover:text-emerald-deep transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

const PodcastsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [podcasts, setPodcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedGenre, setSelectedGenre] = useState(searchParams.get('genre') || 'all');

  const fetchPodcasts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (selectedGenre && selectedGenre !== 'all') params.append('genre', selectedGenre);
      
      const response = await axios.get(`${API}/podcasts?${params.toString()}`);
      setPodcasts(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch podcasts:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPodcasts();
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (selectedGenre && selectedGenre !== 'all') params.set('genre', selectedGenre);
    setSearchParams(params);
  }, [selectedGenre]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchPodcasts();
  };

  return (
    <div className="min-h-screen bg-stone-white" data-testid="podcasts-page">
      <Navbar />

      {/* Header */}
      <div className="pt-24 pb-8 bg-gradient-to-br from-emerald-deep to-emerald-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 text-white px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Podcast className="w-4 h-4" />
            <span>Black Voices, Black Stories</span>
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-white mb-4" data-testid="podcasts-title">
            Discover Black Podcasts
          </h1>
          <p className="text-emerald-100 text-lg max-w-2xl mx-auto">
            Tune into powerful voices from the Black community. Business insights, culture, faith, entertainment, and more.
          </p>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="sticky top-16 z-40 bg-white/95 backdrop-blur-lg border-b border-stone-200 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
              <Input
                type="text"
                placeholder="Search podcasts, hosts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-12 rounded-full border-stone-200"
                data-testid="podcast-search-input"
              />
            </div>
            <Select value={selectedGenre} onValueChange={setSelectedGenre}>
              <SelectTrigger className="w-full sm:w-56 h-12 rounded-full" data-testid="genre-select">
                <SelectValue placeholder="All Genres" />
              </SelectTrigger>
              <SelectContent>
                {GENRES.map((genre) => (
                  <SelectItem key={genre.id} value={genre.id}>
                    {genre.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="submit"
              className="bg-emerald-deep hover:bg-emerald-deep/90 text-white rounded-full px-6 h-12"
              data-testid="podcast-search-btn"
            >
              Search
            </Button>
          </form>
        </div>
      </div>

      {/* Results */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex items-center justify-between">
          <p className="text-stone-600" data-testid="podcast-results-count">
            {loading ? 'Loading...' : `${podcasts.length} podcasts found`}
          </p>
          <Link to="/add-podcast">
            <Button className="bg-emerald-deep hover:bg-emerald-deep/90 text-white rounded-full gap-2">
              <Podcast className="w-4 h-4" />
              Add Your Podcast
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white rounded-xl h-96 animate-pulse" />
            ))}
          </div>
        ) : podcasts.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="podcast-grid">
            {podcasts.map((podcast) => (
              <PodcastCard key={podcast.podcast_id} podcast={podcast} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-white rounded-2xl" data-testid="no-podcasts">
            <Podcast className="w-16 h-16 text-stone-300 mx-auto mb-4" />
            <h3 className="font-display text-xl font-semibold text-stone-700 mb-2">
              No Podcasts Found
            </h3>
            <p className="text-stone-500 mb-6">
              Be the first to add a Black podcast to our directory!
            </p>
            <Link to="/add-podcast">
              <Button className="bg-emerald-deep hover:bg-emerald-deep/90 text-white rounded-full">
                Add Your Podcast
              </Button>
            </Link>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default PodcastsPage;
